/* eslint-disable sonarjs/no-duplicate-string */
import { type WopiParams, EditorID } from 'Cloud/Application/Editor/types';
import hancom_16x16 from 'img/editor/hancom_16x16.png';
import hancom_32x32 from 'img/editor/hancom_32x32.png';
import mailpad_16x16 from 'img/editor/mailpad_16x16.png';
import mailpad_32x32 from 'img/editor/mailpad_32x32.png';
import { addParams } from 'lib/urlUtils';
import { ENVIRONMENT } from 'server/constants/environment';
import { type EditorConfig, type EditorIntegrationConfig, type EditorsIntegrationSettings, EditorMode } from 'server/helpers/editors/types';
import type { IUser } from 'server/helpers/getUser';
import type { IContext, ICtx } from 'server/types/context/IContext';
import type { EEditorUserType } from 'server/types/context/IOnlineConf';
import { EEnvironment } from 'server/types/EEnvironment';

import type { IOmicron } from '../getOmicron';
import { availableEditors } from './getEditors';
import { getEditorSettingsFromOnlineConf } from './getEditorSettingsFromOnlineConf';

export const addProtocol = (url: string) => url.replace(/^\/\//, `${window.location.protocol}//`); // CLOUDWEB-3901

const get_view_url = <
    Item extends {
        stockId?: string;
        id?: string;
        home?: string;
    }
>(
    item: Item,
    storage: string,
    wopiParams: WopiParams
): Promise<string> => {
    const viewUrl = wopiParams?.url
        ? addParams(wopiParams.url, {
              title: 'title',
              lang: 'ru-ru',
              closebutton: 1,
              revisionhistory: 1,
          })
        : `/${(storage === 'stock' ? item.stockId : item.id) || item.home}`;

    return Promise.resolve(viewUrl);
};

const editData = (isEdit: boolean, id: string) => {
    return {
        Status: '200',
        edit_url: `/${id}`,
        action: isEdit ? 'edit' : 'view',
    } as const;
};

// eslint-disable-next-line max-lines-per-function
export const getEditorsConfig = (editorId: EditorID, user?: IUser): EditorConfig | undefined => {
    if (editorId === EditorID.MAILPAD) {
        const documentsRoot = ENVIRONMENT === EEnvironment.DEV ? '//docs.win54.dev.mail.ru/' : '//docs.mail.ru/';

        return {
            id: EditorID.MAILPAD,
            title: 'Редактор Mail.ru',
            description: 'Новое приложение Mail.ru для редактирования документов Word. Доступна альфа-версия с базовым функционалом',
            icon: {
                '16x16': mailpad_16x16,
                '32x32': mailpad_32x32,
            },
            'x-docs-root': documentsRoot,
            documentsRoot,
            get_view_url(item, storage) {
                const url = (storage === 'stock' ? item.stockId : item.id) || item.home;
                const viewUrl = url ? addProtocol(`${documentsRoot}view/?src=${encodeURIComponent(url)}&r=${Math.random()}`) : '';
                return Promise.resolve(viewUrl);
            },
        };
    }

    if (editorId === EditorID.R7) {
        return {
            id: EditorID.R7,
            title: 'Редактор R7',
            description: 'Редактор R7',
            view_url: '/r7/view/',
            edit_uri: '/r-seven/edit/',
            create_uri: '/r-seven/create/',
            edit_storage: 'r-seven/edit',
            edit_data(isEdit, id) {
                return {
                    access_token_ttl: 0,
                    Status: '200',
                    edit_url: `/${id}`,
                    action: isEdit ? EditorMode.EDIT : EditorMode.VIEW,
                    access_token: 'fakeForR7',
                };
            },
            icon: {
                '16x16': hancom_16x16,
                '32x32': hancom_32x32,
            },
            documentsRoot: '/r7/view/',
            get_view_url({ stockId, id, home }, storage) {
                const viewUrl = (storage === 'stock' ? stockId : id) || home;

                return Promise.resolve(`/${viewUrl}`);
            },
        };
    }

    if (editorId === EditorID.MYOFFICE) {
        return {
            id: EditorID.MYOFFICE,
            title: 'Редактор Мой офис',
            description: 'Редактор Мой офис',
            wopi: true,
            view_url: '/myoffice/view/',
            edit_uri: '/myoffice/edit/',
            create_uri: '/myoffice/',
            edit_storage: 'myoffice/edit/home' || 'myoffice/edit/attaches',
            edit_data(isEdit, id) {
                return {
                    action: isEdit ? EditorMode.EDIT : EditorMode.VIEW,
                    edit_url: `/${id}`,
                };
            },
            icon: {
                '16x16': hancom_16x16,
                '32x32': hancom_32x32,
            },
            documentsRoot: '/myoffice/view/',
            get_view_url,
        };
    }

    if (editorId === EditorID.DOCS_MAIL) {
        return {
            id: EditorID.DOCS_MAIL,
            title: 'docs.mail.ru',
            description: 'docs.mail.ru',
            icon: {},
            documentsRoot: '//docs.mail.ru/',
        };
    }

    // TODO CLOUDWEB-15451 убрать проверку "user && isAnonymUser" когда Ovidius будет работать на анонимных пользователях (ВК)
    if (editorId === EditorID.OVIDIUS_V2 && user && !user.isAnonymUser) {
        return {
            id: EditorID.OVIDIUS_V2,
            title: 'Ovidius',
            description: 'Ovidius',
            icon: {},
            documentsRoot: '/ovidiusV2/view',
        };
    }

    if (editorId === EditorID.R7_WOPI) {
        return {
            id: EditorID.R7_WOPI,
            title: 'Редактор R7 WOPI',
            description: 'r7 wopi',
            wopi: true,
            icon: {},
            documentsRoot: '',
            edit_uri: '/r7w/edit/',
            edit_storage: 'r7w/edit/home',
            edit_data: editData,
            get_view_url: ({ stockId, id, home }, storage, wopiParams) => {
                const viewUrl = wopiParams?.url ? wopiParams.url : `/${(storage === 'stock' ? stockId : id) || home}`;

                return Promise.resolve(viewUrl);
            },
        };
    }
};

export const getEditorIntegrationConfig = (
    ctx: ICtx,
    json: IContext,
    userGroup: EEditorUserType,
    omicron: IOmicron
): EditorsIntegrationSettings => {
    const editorsConfig = availableEditors.reduce((acc, editorId) => {
        const onlineconf = getEditorSettingsFromOnlineConf({ ctx, context: json, userGroupName: userGroup, editorId }).cfg;
        const omicronConfig = omicron?.config?.[editorId];

        acc[editorId] = { omicronConfig, onlineconf };

        return acc;
    }, {} as EditorIntegrationConfig);

    return { userGroup, editorsConfig };
};
